import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Select from '@amzn/awsui-components-core/select';
import Spinner from '@amzn/awsui-components-core/spinner';
import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-core/status-indicator';
import React from 'react';
import { useStarGridData } from '../context/StarGridDataContext';
import { HOME_SORT_OPTIONS, useHomeViewState } from '../hooks/useHomeViewState';
import { PnlReportSummary } from '../starGridTypes';
import { computeStatusCounts, formatRowCount, normalizeReportStatus, relativeTime, reportStatusLabel } from '../utils/reportStatus';
import styles from './styles/StarGridHomeView.module.scss';

export interface StarGridHomeViewProps {
  /** Called when the user clicks Create Report — parent routes to the create-report view. */
  onCreate?: () => void;
  /** Called when a report card is opened — parent routes to the detail view. */
  onOpenReport?: (reportId: number) => void;
}

/** Card status indicator mapping — pill color + Cloudscape StatusIndicator type + label. */
const STATUS_INDICATOR_TYPE: Record<string, StatusIndicatorProps.Type> = {
  READY: 'success',
  GENERATING: 'in-progress',
  FAILED: 'error',
  DELETED: 'stopped'
};

const STATUS_CLASSNAME: Record<string, string> = {
  READY: styles.statusReady,
  GENERATING: styles.statusGenerating,
  FAILED: styles.statusFailed
};

const ReportCard: React.FC<{ report: PnlReportSummary; onOpen: () => void }> = ({ report, onOpen }) => {
  const status = normalizeReportStatus(report.status);
  const dims = report.mv_name ? [] : []; // reserved: we could enrich with definition on hover
  return (
    <div
      className={styles.card}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle} title={report.report_name}>
          {report.report_name}
        </div>
        <div className={`${styles.cardStatus} ${STATUS_CLASSNAME[status] ?? ''}`}>
          <StatusIndicator type={STATUS_INDICATOR_TYPE[status] ?? 'pending'}>{reportStatusLabel(status)}</StatusIndicator>
        </div>
      </div>

      <div className={styles.cardMeta}>
        <span>
          <strong>{formatRowCount(report.row_count)}</strong> rows
        </span>
        {report.mv_last_refresh_duration ? (
          <span>
            <strong>{report.mv_last_refresh_duration}</strong> s refresh
          </span>
        ) : null}
        {report.source_mode ? (
          <span>
            <strong>{report.source_mode === 'INGESTED' ? 'Native' : 'External'}</strong>
          </span>
        ) : null}
      </div>

      {dims.length > 0 ? (
        <div className={styles.dimTags}>
          {dims.map((d) => (
            <span key={d} className={styles.dimTag}>
              {d}
            </span>
          ))}
        </div>
      ) : (
        <div className={styles.dimTags} />
      )}

      <div className={styles.cardFooter}>
        <div className={styles.cardAuthor}>
          {report.created_by ?? '—'} · {relativeTime(report.created_at)}
        </div>
        <div className={styles.cardLink}>
          <Button variant="inline-link" onClick={onOpen}>
            View Config →
          </Button>
        </div>
      </div>
    </div>
  );
};

/** P&L Reports landing view — card grid, status pill row, sort + refresh controls. */
export const StarGridHomeView: React.FC<StarGridHomeViewProps> = ({ onCreate, onOpenReport }) => {
  const { pnlReports } = useStarGridData();
  const reports = pnlReports.data ?? [];
  const { sortKey, setSortKey, visibleReports } = useHomeViewState(reports);
  const counts = computeStatusCounts(reports);
  const selectedSort = HOME_SORT_OPTIONS.find((o) => o.value === sortKey) ?? HOME_SORT_OPTIONS[0];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>P&amp;L Reports</h1>
          <p className={styles.subtitle}>Configure scenarios, dimensions, and measures. Open a report to explore its data.</p>
        </div>
        <div className={styles.headerActions}>
          <Button iconName="refresh" onClick={() => void pnlReports.refresh()} loading={pnlReports.loading} data-testid="stargrid-home-refresh">
            Refresh
          </Button>
          <Button variant="primary" iconName="add-plus" onClick={onCreate} data-testid="stargrid-home-create">
            Create Report
          </Button>
        </div>
      </header>

      <div className={styles.pillRow}>
        <span className={`${styles.pill} ${styles.pillTotal}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.total}</span> Total
        </span>
        <span className={`${styles.pill} ${styles.pillReady}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.ready}</span> Ready
        </span>
        <span className={`${styles.pill} ${styles.pillGenerating}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.generating}</span> Generating
        </span>
        <span className={`${styles.pill} ${styles.pillFailed}`}>
          <span className={styles.pillDot} />
          <span className={styles.pillCount}>{counts.failed}</span> Failed
        </span>
      </div>

      {pnlReports.error && (
        <Alert type="error" header="Could Not Load Reports">
          {pnlReports.error}
        </Alert>
      )}

      <div className={styles.toolbar}>
        <div className={styles.countLabel}>
          Showing <strong>{visibleReports.length}</strong> of <strong>{counts.total}</strong> reports
        </div>
        <div className={styles.sortWrap}>
          <label htmlFor="stargrid-sort-select">Sort By</label>
          <Select
            selectedOption={{ value: selectedSort.value, label: selectedSort.label }}
            options={HOME_SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            onChange={({ detail }) => {
              const val = detail.selectedOption.value;
              if (val === 'recent' || val === 'oldest' || val === 'name' || val === 'status') {
                setSortKey(val);
              }
            }}
            data-testid="stargrid-home-sort"
          />
        </div>
      </div>

      {pnlReports.loading && !reports.length ? (
        <Box textAlign="center" padding="l">
          <Spinner /> Loading reports…
        </Box>
      ) : visibleReports.length === 0 ? (
        <div className={styles.empty}>
          <Box variant="p">
            No reports yet. Click <strong>Create Report</strong> to build your first one.
          </Box>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {visibleReports.map((report) => (
            <ReportCard key={report.pnl_report_id} report={report} onOpen={() => onOpenReport?.(report.pnl_report_id)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default StarGridHomeView;
